import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import AddLiabilityForm from "components/widgets/forms/setup/Add_Liability";
import TableComponent from "components/widgets/table";
import { fetch_product_details, addProductLiabilites } from "services/apiSrv";
import Loader from "components/widgets/loader";
import { useParams, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { Card, Typography, Avatar, Badge, Button, Space, message } from 'antd';
import * as Icon from 'react-feather';

const Container = styled.div`
  padding: 24px;
  max-width: 100%;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 16px;
  color: #333;
`;

const TabContentCard = styled(Card)`
  border-radius: 12px;
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  background: #fff;
  padding: 16px;
  margin-bottom: 24px;
`;

const SaveButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 12px 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  display: block;
  margin: 32px auto 0;
  transition: background 0.2s;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #95de64;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export default function LiabilityConfig() {
  const { id: productSystemRef } = useParams();
  const history = useHistory();
  const [saving, setSaving] = useState(false);
  const [liabilities, setLiabilities] = useState([]);
  const addLiabilityRef = useRef(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['productDetails', productSystemRef],
    queryFn: () => fetch_product_details(productSystemRef),
    enabled: !!productSystemRef,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    select: (res) => res?.data,
  });

  // Sync liabilities from server when data loads
  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      setLiabilities([]);
      return;
    }
    const product = data[0];
    const fetched = product?.productLiability || [];
    setLiabilities(Array.isArray(fetched) ? fetched : []);
  }, [data]);

  // ─────────────────────────────────────
  // CRITICAL: Get the absolute latest items from the form
  // ─────────────────────────────────────
  const getLatestLiabilities = () => {
    // Method 1: Form exposes getCurrentItems() → Most reliable
    if (addLiabilityRef.current?.getCurrentItems) {
      return addLiabilityRef.current.getCurrentItems();
    }

    // Method 2: Fallback to internal items prop (if exposed)
    if (addLiabilityRef.current?.items && Array.isArray(addLiabilityRef.current.items)) {
      return addLiabilityRef.current.items;
    }

    // Method 3: Last resort — use parent state
    return liabilities;
  };

  const handleSave = async () => {
    const latestLiabilities = getLatestLiabilities();

    console.log("Latest liabilities from form:", latestLiabilities); // ← DEBUG THIS

    if (!latestLiabilities || latestLiabilities.length === 0) {
      message.warning("No liabilities to save.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        productSystemRef,
        liabilities: latestLiabilities
          .filter(item => item.name && item.value != null) // Only valid items
          .map(({ id, ...item }) => ({
            ...item,
            value: Number(item.value) || 0,
          })),
      };

      console.log("Sending payload to API:", payload); // ← CRITICAL DEBUG

      await addProductLiabilites(payload);

      message.success("Liabilities saved successfully!");
      refetch(); // Refresh from server
    } catch (err) {
      console.error("Save failed:", err.response || err);
      message.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save liabilities. Check console."
      );
    } finally {
      setSaving(false);
    }
  };

  // Loading & Error States
  if (isLoading) return <Loader />;
  if (isError) {
    return <Container><p style={{ color: 'red' }}>Error: {error?.message || 'Failed to load product'}</p></Container>;
  }
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <Container><p>No product data available.</p></Container>;
  }

  const product = data?.[0] ?? {};
  
  const {
    productName = 'Unknown Product',
    productDescription = 'No description available',
    productPremium = {}
  } = product;

  const liabilityTableFields = [
    { name: "Name" },
    { name: "Description" },
    // { name: "Value" },
    { name: "Action" },
  ];

  const editLiability = (id) => {
    addLiabilityRef.current?.editItemById?.(id);
  };

  const removeLiability = (id) => {
    setLiabilities(prev => prev.filter(item => item.id !== id));
  };

  const liabilityRowData = [
    { type: "text", field: "name" },
    { type: "text", field: "description" },
    // { type: "number", field: "value" },
    {
      type: "action",
      actions: [
        { method: editLiability, icon: <Icon.Edit2 size={16} />, title: "Edit", type: "primary" },
        { method: removeLiability, icon: <Icon.Trash2 size={16} />, title: "Remove", type: "danger" },
      ],
    },
  ];

  const liabilitiesDisplay = liabilities.map(l => ({
    ...l,
    description: typeof l.description === 'object'
      ? (l.description.action || l.description.text || '')
      : (l.description || ''),
  }));

  return (
    <Container>
      {/* Product Header */}
      <Card style={{ marginBottom: 24, borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Avatar size={96} style={{ backgroundColor: '#e6f7ff', color: '#1890ff', fontSize: 32, fontWeight: 'bold' }}>
              {productName ? productName.split(' ').map(n => n[0].toUpperCase()).slice(0, 2).join('') : 'NA'}
            </Avatar>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 14, display: 'block' }}>Product</Typography.Text>
              <Typography.Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                {productName}
              </Typography.Title>
              <div style={{ color: '#6b7280', marginTop: 8, maxWidth: 600 }}>
                {productDescription}
              </div>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: 16 }}>
              <Typography.Text style={{ color: '#8b949e' }}>Status</Typography.Text>
              <div>
                <Badge status="processing" color="#1890ff" text={<strong>{productPremium?.status || 'ACTIVE'}</strong>} />
              </div>
            </div>
            <Space style={{ marginTop: 16 }}>
              <Button icon={<Icon.Eye size={16} />} onClick={() => history.goBack()}>View Product</Button>
              <Button onClick={() => history.goBack()} icon={<Icon.ArrowLeft size={16} />}>Back</Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* Liabilities Section */}
      <TabContentCard>
        <Title>Liability Coverage Configuration</Title>

        <div style={{ marginBottom: 24 }}>
          <AddLiabilityForm
            ref={addLiabilityRef}
            items={liabilities}
            onItemsChange={setLiabilities}
            refetchProductDetails={refetch}
          />
        </div>

        <TableComponent
          table_fields={liabilityTableFields}
          table_data={liabilitiesDisplay}
          row_data={liabilityRowData}
          title="Configured Liabilities"
          emptyText="No liabilities added yet. Use the form above to add one."
        />
      </TabContentCard>

      {/* Save Button */}
      <SaveButton
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? "Saving..." : "Save Liabilities"}
      </SaveButton>
    </Container>
  );
}